/**
 * EVAP PACK smartlist config — the port of `SMARTLIST_TYPES`
 * (evap_pack.selector.js:43-92) and `_buildCountQS` (:635-644).
 *
 * Legacy declares FIVE types (media, pump, fill, drain, overflow) but only
 * media and pump have a `.smartlist-count-row` button strip and a
 * `mounting-flip-back` panel in the template — the other three are config-only,
 * carry `noFetchCount: true`, and have no UI to drive them. So only the two
 * with a front end are ported here; adding the rest means adding template
 * buttons and panels first, exactly as the legacy comment says.
 *
 * `paramFields` is what a smartlist pick writes into `<type>_params`: legacy
 * REPLACES the params object rather than merging, keeping only these keys
 * (`selectFromSmartlist` :1040-1046).
 */

export type EvapSmartlistType = 'media' | 'pump';

export type EvapSmartlistCfg = {
  itemsBase: string;
  countBase: string;
  /** the staging field holding the picked catalogue row's id */
  masterField: string;
  /** the staging JSON blob the pick rewrites */
  paramsKey: string;
  /** the item keys copied into paramsKey on a pick */
  paramFields: string[];
};

/** Legacy `SMARTLIST_PAGE_SIZE` (:38) — the has-more sentinel, not a limit we send. */
export const EVAP_SMARTLIST_PAGE_SIZE = 50;

export const EVAP_SMARTLIST_TYPES: Record<EvapSmartlistType, EvapSmartlistCfg> = {
  media: {
    itemsBase: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/medias/all/',
    countBase: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/medias/all/count/',
    masterField: 'master_media_id',
    paramsKey: 'media_params',
    paramFields: ['mfg_id', 'model_id', 'edge_finish_id', 'depth_id'],
  },
  pump: {
    itemsBase: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/pumps/all/',
    countBase: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/pumps/all/count/',
    masterField: 'master_pump_id',
    paramsKey: 'pump_params',
    paramFields: ['type_id', 'mfg_id', 'model', 'power', 'voltage'],
  },
};

/**
 * Legacy `_buildCountQS` (:635-644) — the saved `<type>_params` blob rendered
 * as a query string, dropping null / '' / 'None'. Both the items and the
 * filtered-count URLs are built from it, so they always agree.
 */
export function buildEvapParamsQs(params: Record<string, unknown>): string {
  return Object.entries(params)
    .filter(([, v]) => v != null && v !== '' && v !== 'None')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
}

/**
 * The three URLs a load needs: the filtered item page, the filtered count and
 * the unfiltered total (the `x / y` badge's denominator). Paging is `page=N`,
 * 1-indexed, appended after the params (`loadMoreSmartlist` :847).
 */
export function buildEvapSmartlistUrls(
  type: EvapSmartlistType,
  params: Record<string, unknown>,
  page = 1
): { itemsUrl: string; countUrl: string; totalUrl: string } {
  const cfg = EVAP_SMARTLIST_TYPES[type];
  const qs = buildEvapParamsQs(params);
  const itemsQs = page > 1 ? `${qs ? `${qs}&` : ''}page=${page}` : qs;
  return {
    itemsUrl: cfg.itemsBase + (itemsQs ? `?${itemsQs}` : ''),
    countUrl: cfg.countBase + (qs ? `?${qs}` : ''),
    totalUrl: cfg.countBase,
  };
}
